<script lang="ts">
  import PersonalTools from './PersonalTools.svelte'
  import type { OpsPayload } from '../../shared/contract'
  import Groomed from './Groomed.svelte'
  import { filterTitle, filtering, liftsLine, perArea, type Filter, type Mountain } from './tally'
  import { clockTime, freshnessParts, hourText, type Opening } from './when'

  /**
   * Like wx: the time, a quiet freshness line, an hours lamp and one light
   * headline. With a filter on, the headline is the match as a sentence.
   */
  let {
    data,
    m,
    now,
    quiet,
    live = true,
    opens,
    filter,
    matched,
    openMode = false,
    lamps = false,
    liftsShown,
    onclear,
  }: {
    data: OpsPayload
    m: Mountain
    now: Date
    /** One quiet word for the freshness line ("offline"). */
    quiet?: string
    /** This session has heard from the edge: the line may say Live. */
    live?: boolean
    /** Opening day, while it is still ahead and nothing is turning. */
    opens: Opening | null
    filter: Filter
    matched: number
    /** An area is open on a phone: short screens give the headline's room to the list. */
    openMode?: boolean
    /** The plates show lamps without lift names: this header names the shorter list. */
    lamps?: boolean
    /** How many lift names fit on the lifts line; the rest become "+N more". */
    liftsShown?: number
    onclear: () => void
  } = $props()

  const resort = $derived(data.resort)
  const isOpen = $derived(resort.status === 'open')
  const f = $derived(filtering(filter))
  /** Closed with nothing turning: preseason, or the season over. */
  const closed = $derived(resort.status === 'closed' && m.turning === 0)
  /** Preseason only while opening day is ahead: the worker sends its date all year. */
  const hours = $derived(
    isOpen
      ? resort.opens && resort.closes
        ? `${hourText(resort.opens)} – ${hourText(resort.closes)}`
        : 'open'
      : opens
        ? 'preseason'
        : 'closed',
  )
  const fresh = $derived(freshnessParts(data.checkedAt, data.changedAt, now, quiet, live))
  const per = $derived(perArea(data.areas, filter))
  const lifts = $derived(liftsLine(m.lifts))
  const liftNames = $derived(
    lifts.names && liftsShown != null ? lifts.names.slice(0, liftsShown) : lifts.names,
  )
  /** The report's own note, as a headline: without its closing full stop. */
  const note = $derived(resort.note?.replace(/\.\s*$/, ''))
</script>

<header class:filtering={f} class:open-mode={openMode} class:lamps>
  <div class="clock">{clockTime(now)}<PersonalTools {data} /></div>
  <div class="hours" class:on={isOpen}><i aria-hidden="true"></i><span>{hours}</span></div>
  <!-- A narrow line drops the "changed" part whole, onto a second line that is never shown. -->
  <div class="fresh">
    <span class="lead">{fresh.lead}</span>{#if fresh.changed}<span class="chg"
        >{' '}<span aria-hidden="true">·</span> {fresh.changed}</span
      >{/if}
  </div>
  {#if f}
    <h1 class="long">{filterTitle(matched, filter)}</h1>
    <div class="sub">
      {#if per.length > 3}
        <span class="in">in {per.length} of {data.areas.length} areas</span>
      {:else if per.length}
        <span class="mono">In</span>
        <span class="in">
          {#each per as [name, n], i}{i ? ' · ' : ''}{name} <b>{n}</b>{/each}
        </span>
      {:else}
        <span class="in">none on the mountain</span>
      {/if}
      <button class="clear mono" onclick={onclear}>Clear</button>
    </div>
  {:else if closed && opens}
    <h1>{opens.title}</h1>
    <div class="sub">
      <span class="mono">Season</span>
      <span class="in">{opens.when} · {m.lifts.length} lifts · {m.runs.length} runs</span>
    </div>
  {:else if closed && !m.anyOpen}
    <h1 class:long={!!note}>{note || 'Closed'}</h1>
    <div class="sub">
      <span class="mono">Season</span>
      <span class="in">{m.lifts.length} lifts · {m.runs.length} runs</span>
    </div>
  {:else}
    <h1>{m.turning} <span class="of">of {m.lifts.length}</span> lifts turning</h1>
    <div class="sub">
      <span class="mono">Runs</span>
      <span><b>{m.open}</b> of {m.runs.length} open</span>
      <span>·</span>
      <Groomed />
      <span><b>{m.groomed}</b> groomed</span>
    </div>
  {/if}
  <div class="liftsx">
    <span class="mono">{lifts.label}</span>{#if liftNames && lifts.names}<b
        >{liftNames.join(' · ')}</b
      >{#if liftNames.length < lifts.names.length}{liftNames.length ? ' · ' : ''}<span
          class="more">+{lifts.names.length - liftNames.length} more</span
        >{/if}{:else}{lifts.text}{/if}
  </div>
</header>

<style>
  header {
    flex: none;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 12px;
  }
  .clock {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    line-height: 20px;
  }
  .fresh {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.3em;
    height: 16px;
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    color: var(--ink-2);
    white-space: nowrap;
  }
  .fresh .lead {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hours {
    align-self: start;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: var(--ink-2);
    padding-top: 2px;
    white-space: nowrap;
  }
  .hours i {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1.5px solid var(--ink-3);
    flex: none;
  }
  .hours.on i {
    border-color: var(--amber);
    background: var(--amber);
    box-shadow: 0 0 0 3px var(--amber-halo);
  }
  h1 {
    grid-column: 1 / -1;
    margin-top: 12px;
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.005em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  h1 .of {
    color: var(--ink-2);
  }
  h1.long {
    font-size: 21px;
  }
  .sub {
    grid-column: 1 / -1;
    margin-top: 1px;
    min-height: 22px;
    font-size: 14px;
    line-height: 22px;
    color: var(--ink-2);
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }
  .sub .mono {
    color: var(--ink-2);
    flex: none;
  }
  .sub b {
    color: var(--ink);
    font-weight: 400;
  }
  /* 44 px tall in a 22 px line: it reaches over the headline, so it sits on top. */
  .sub .clear {
    position: relative;
    margin-left: auto;
    height: 44px;
    min-width: 44px;
    margin-block: -11px;
    padding: 0 2px 0 12px;
    color: var(--amber-ink);
    flex: none;
  }
  .sub .in {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .liftsx {
    display: none;
    grid-column: 1 / -1;
    font-size: 13px;
    line-height: 18px;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
  }
  .lamps:not(.filtering):not(.open-mode) .liftsx {
    display: block;
  }
  .liftsx .more {
    font: 400 11px/18px var(--mono);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .liftsx .mono {
    margin-right: 6px;
  }
  .liftsx b {
    color: var(--ink);
    font-weight: 400;
  }

  /* Short phones (375 × 667): closed lifts named here, lamps in the plates. */
  @media (max-width: 999px) and (max-height: 760px) {
    .hours {
      padding-top: 0;
      font-size: 13px;
    }
    h1 {
      margin-top: 6px;
      font-size: 22px;
      line-height: 27px;
    }
    h1.long {
      font-size: 19px;
    }
    .sub {
      font-size: 13px;
      line-height: 20px;
      min-height: 20px;
    }
    .liftsx {
      display: block;
    }
    .open-mode h1,
    .open-mode .sub,
    .open-mode .liftsx,
    .filtering .liftsx {
      display: none;
    }
  }
  @media (min-width: 1000px) and (min-height: 620px) {
    header {
      grid-column: 1;
      grid-row: 1;
    }
  }
</style>
